import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Building, CalendarClock, CheckCircle2, Ban } from "lucide-react";

const METHODS = [
  { v: "straight_line", l: "定額法" },
  { v: "declining_balance", l: "定率法 (200%)" },
];
const DEP_TYPES = [
  { v: "normal", l: "通常償却 (定額/定率)" },
  { v: "immediate", l: "少額減価償却資産 (30万円未満・即時償却)" },
  { v: "lump_sum", l: "一括償却資産 (20万円未満・3年均等)" },
];
const emptyForm = {
  name: "", category: "", asset_account: "工具器具備品", acquisition_date: "",
  acquisition_cost: "", useful_life: 5, method: "straight_line", depreciation_type: "normal", memo: "",
};

export default function FixedAssets() {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null); // schedule dialog
  const [disposeFor, setDisposeFor] = useState(null);
  const [dispose, setDispose] = useState({ disposal_date: "", disposal_type: "retire", proceeds: "" });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/billing/fixed-assets"), api.get("/billing/fixed-assets/summary")])
      .then(([a, s]) => { setAssets(a.data || []); setSummary(s.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setFormOpen(true); };
  const openEdit = (a) => {
    setForm({ name: a.name, category: a.category || "", asset_account: a.asset_account || "工具器具備品", acquisition_date: a.acquisition_date, acquisition_cost: a.acquisition_cost, useful_life: a.useful_life, method: a.method, depreciation_type: a.depreciation_type || "normal", memo: a.memo || "" });
    setEditId(a.id); setFormOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.acquisition_date || !form.acquisition_cost || !form.useful_life) {
      toast.error("資産名・取得日・取得価額・耐用年数は必須です"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, acquisition_cost: Number(form.acquisition_cost), useful_life: Number(form.useful_life) };
      if (editId) await api.put(`/billing/fixed-assets/${editId}`, payload);
      else await api.post(`/billing/fixed-assets`, payload);
      setFormOpen(false); load(); toast.success("保存しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`「${a.name}」を削除しますか？`)) return;
    try { await api.delete(`/billing/fixed-assets/${a.id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSchedule = async (a) => {
    setDetail({ loading: true, asset: a });
    try {
      const r = await api.get(`/billing/fixed-assets/${a.id}/schedule`);
      setDetail({ loading: false, ...r.data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setDetail(null); }
  };

  const depreciate = async (fy) => {
    if (!window.confirm(`${fy}年度の減価償却仕訳候補(借:減価償却費/貸:減価償却累計額)を作成しますか？`)) return;
    setBusy(true);
    try {
      await api.post(`/billing/fixed-assets/${detail.asset.id}/depreciate`, { fiscal_year: fy });
      toast.success("減価償却の仕訳候補を作成しました（会計連携で承認）");
      await openSchedule(detail.asset); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openDispose = (a) => {
    setDisposeFor(a);
    setDispose({ disposal_date: new Date().toISOString().slice(0, 10), disposal_type: "retire", proceeds: "" });
  };
  const doDispose = async () => {
    if (!dispose.disposal_date) { toast.error("除却・売却日を入力してください"); return; }
    setBusy(true);
    try {
      await api.post(`/billing/fixed-assets/${disposeFor.id}/dispose`, {
        disposal_date: dispose.disposal_date, disposal_type: dispose.disposal_type,
        proceeds: Number(dispose.proceeds) || 0,
      });
      setDisposeFor(null); load(); toast.success("除却・売却の仕訳候補を作成しました（会計連携で承認）");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="fixed-assets-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-700 flex items-center gap-1.5"><Building className="h-4 w-4" /> 固定資産</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">固定資産台帳・減価償却</h1>
          <p className="mt-1 text-sm text-stone-500">定額法・定率法(200%)に対応。年度ごとに減価償却の仕訳候補を作成できます。</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700" data-testid="fa-new-button"><Plus className="h-4 w-4" /> 資産を登録</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="登録資産数" value={summary?.count} raw testid="fa-sum-count" />
        <SummaryCard label="取得価額合計" value={summary?.total_cost} testid="fa-sum-cost" />
        <SummaryCard label="減価償却累計額" value={summary?.total_accumulated} accent="amber" testid="fa-sum-acc" />
        <SummaryCard label="帳簿価額" value={summary?.net_book_value} accent="emerald" testid="fa-sum-nbv" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="fa-table">
            <thead className="bg-stone-50 text-stone-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2">資産名</th><th className="text-left px-3 py-2">勘定科目</th>
                <th className="text-left px-3 py-2">取得日</th><th className="text-right px-3 py-2">取得価額</th>
                <th className="text-center px-3 py-2">耐用年数</th><th className="text-left px-3 py-2">償却方法</th>
                <th className="text-right px-3 py-2">償却累計/帳簿価額</th><th className="text-right px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {assets.length ? assets.map((a) => {
                const disposed = a.status === "disposed";
                const dtLabel = a.depreciation_type === "immediate" ? "即時償却"
                  : a.depreciation_type === "lump_sum" ? "一括3年"
                  : (a.method === "straight_line" ? "定額法" : "定率法");
                return (
                <tr key={a.id} className={`border-t border-stone-100 ${disposed ? "opacity-60" : ""}`} data-testid={`fa-row-${a.id}`}>
                  <td className="px-3 py-2 text-stone-800">{a.name}
                    <span className="block text-[11px] text-stone-400">{a.category}{disposed ? <span className="ml-1 text-red-500">・{a.disposal_type === "sell" ? "売却済" : "除却済"}</span> : null}</span>
                  </td>
                  <td className="px-3 py-2 text-stone-500">{a.asset_account}</td>
                  <td className="px-3 py-2 font-numbers text-stone-500">{a.acquisition_date}</td>
                  <td className="px-3 py-2 text-right font-numbers">{formatJPY(a.acquisition_cost)}</td>
                  <td className="px-3 py-2 text-center font-numbers">{a.useful_life}年</td>
                  <td className="px-3 py-2"><span className="text-[11px] rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5">{dtLabel}</span></td>
                  <td className="px-3 py-2 text-right font-numbers text-xs text-stone-500">{formatJPY(a.accumulated_depreciation)} <span className="text-stone-300">/</span> {formatJPY(a.acquisition_cost - a.accumulated_depreciation)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openSchedule(a)} className="text-indigo-600 hover:underline text-xs mr-2" data-testid={`fa-schedule-${a.id}`}>償却表</button>
                    {!disposed && <button onClick={() => openDispose(a)} className="text-amber-600 hover:underline text-xs mr-2" data-testid={`fa-dispose-${a.id}`}>除却/売却</button>}
                    <button onClick={() => openEdit(a)} className="text-stone-600 hover:underline text-xs mr-2">編集</button>
                    <button onClick={() => del(a)} className="text-red-500 hover:underline text-xs">削除</button>
                  </td>
                </tr>
                );
              }) : <tr><td colSpan={8} className="text-center text-stone-400 py-10">固定資産が登録されていません</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="fa-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "固定資産の編集" : "固定資産の登録"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">資産名</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fa-name" placeholder="例: ノートPC" /></div>
            <div><Label className="text-xs">種別 / カテゴリ</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="例: 備品" /></div>
            <div><Label className="text-xs">資産勘定科目</Label><Input value={form.asset_account} onChange={(e) => setForm({ ...form, asset_account: e.target.value })} data-testid="fa-account" /></div>
            <div><Label className="text-xs">取得日</Label><Input type="date" value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} data-testid="fa-date" /></div>
            <div><Label className="text-xs">取得価額(円)</Label><Input type="number" value={form.acquisition_cost} onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })} data-testid="fa-cost" /></div>
            <div><Label className="text-xs">耐用年数(年)</Label><Input type="number" value={form.useful_life} onChange={(e) => setForm({ ...form, useful_life: e.target.value })} data-testid="fa-life" /></div>
            <div className="col-span-2"><Label className="text-xs">償却区分</Label>
              <Select value={form.depreciation_type} onValueChange={(v) => setForm({ ...form, depreciation_type: v })}>
                <SelectTrigger data-testid="fa-dep-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DEP_TYPES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.depreciation_type === "normal" && (
              <div className="col-span-2"><Label className="text-xs">償却方法</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="fa-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2"><Label className="text-xs">備考</Label><Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setFormOpen(false)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={save} disabled={busy} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="fa-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} 保存</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="fa-schedule-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-indigo-600" /> 減価償却スケジュール</DialogTitle></DialogHeader>
          {detail?.loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
          ) : detail ? (
            <div className="space-y-3">
              <div className="text-sm text-stone-600">{detail.asset?.name}・{detail.method_label}・取得価額 {formatJPY(detail.asset?.acquisition_cost)}・耐用年数 {detail.asset?.useful_life}年</div>
              <div className="rounded-lg border border-stone-200 overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-500 text-xs sticky top-0"><tr><th className="text-left px-3 py-2">年度</th><th className="text-center px-3 py-2">月数</th><th className="text-right px-3 py-2">期首簿価</th><th className="text-right px-3 py-2">償却額</th><th className="text-right px-3 py-2">期末簿価</th><th className="text-right px-3 py-2">操作</th></tr></thead>
                  <tbody>
                    {detail.rows?.map((r) => {
                      const done = (detail.depreciated_years || []).includes(r.fiscal_year);
                      return (
                        <tr key={r.fiscal_year} className="border-t border-stone-100" data-testid={`fa-sched-${r.fiscal_year}`}>
                          <td className="px-3 py-2 font-numbers">{r.label}</td>
                          <td className="px-3 py-2 text-center font-numbers text-stone-500">{r.months}</td>
                          <td className="px-3 py-2 text-right font-numbers">{formatJPY(r.opening)}</td>
                          <td className="px-3 py-2 text-right font-numbers font-semibold text-indigo-700">{formatJPY(r.depreciation)}</td>
                          <td className="px-3 py-2 text-right font-numbers">{formatJPY(r.closing)}</td>
                          <td className="px-3 py-2 text-right">
                            {done ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> 作成済</span>
                            ) : (
                              <button onClick={() => depreciate(r.fiscal_year)} disabled={busy} className="text-indigo-600 hover:underline text-xs disabled:opacity-50" data-testid={`fa-depreciate-${r.fiscal_year}`}>仕訳作成</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-stone-400">「仕訳作成」で 借:減価償却費 / 貸:減価償却累計額 の仕訳候補を作成します。承認は会計連携画面で行います。</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dispose 除却/売却 */}
      <Dialog open={!!disposeFor} onOpenChange={(o) => !o && setDisposeFor(null)}>
        <DialogContent data-testid="fa-dispose-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="h-5 w-5 text-amber-600" /> 固定資産の除却・売却</DialogTitle></DialogHeader>
          {disposeFor && (
            <div className="space-y-3">
              <div className="text-sm text-stone-600">{disposeFor.name}・取得価額 {formatJPY(disposeFor.acquisition_cost)}・帳簿価額 {formatJPY(disposeFor.acquisition_cost - disposeFor.accumulated_depreciation)}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">区分</Label>
                  <Select value={dispose.disposal_type} onValueChange={(v) => setDispose({ ...dispose, disposal_type: v })}>
                    <SelectTrigger data-testid="fa-dispose-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="retire">除却 (固定資産除却損)</SelectItem>
                      <SelectItem value="sell">売却 (固定資産売却損益)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">除却・売却日</Label><Input type="date" value={dispose.disposal_date} onChange={(e) => setDispose({ ...dispose, disposal_date: e.target.value })} data-testid="fa-dispose-date" /></div>
                {dispose.disposal_type === "sell" && (
                  <div className="col-span-2"><Label className="text-xs">売却額(円)</Label><Input type="number" value={dispose.proceeds} onChange={(e) => setDispose({ ...dispose, proceeds: e.target.value })} data-testid="fa-dispose-proceeds" /></div>
                )}
              </div>
              <p className="text-[11px] text-stone-400">仕訳候補: 借 減価償却累計額{dispose.disposal_type === "sell" ? " ＋ 普通預金(売却額) ＋ 固定資産売却損" : " ＋ 固定資産除却損(帳簿価額)"} / 貸 {disposeFor.asset_account}{dispose.disposal_type === "sell" ? " ＋ 固定資産売却益" : ""}。承認は会計連携画面で行います。</p>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDisposeFor(null)} className="px-4 py-2 text-sm text-stone-600">キャンセル</button>
            <button onClick={doDispose} disabled={busy} className="px-5 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-60 flex items-center gap-2" data-testid="fa-dispose-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} 実行</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, accent, raw, testid }) {
  const map = { emerald: "text-emerald-700", amber: "text-amber-700", indigo: "text-indigo-700" };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-stone-500">{label}</div>
      <div className={`mt-1 font-numbers font-bold text-xl ${map[accent] || "text-stone-900"}`}>{raw ? (value ?? 0) : formatJPY(value || 0)}</div>
    </div>
  );
}
